import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../App';

const C = {
  bg: '#0D0D12', surface: '#16161F', card: '#1C1C28', border: '#2A2A3A',
  accent: '#E8A2B8', teal: '#7ECECA', gold: '#D4AF7A',
  text: '#F0EEF5', muted: '#8A8A9E', danger: '#FF6B7A', green: '#7ECBA5', purple: '#B39DDB',
};

const FREQUENCIES = ['Daily', 'Twice daily', 'As needed', 'Weekly', 'Other'];

export default function Medications() {
  const [medications, setMedications] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [dose, setDose] = useState('');
  const [frequency, setFrequency] = useState('Daily');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const token = localStorage.getItem('authToken');

  const fetchMeds = () => {
    axios.get(`${API_URL}/api/medications`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setMedications(r.data))
      .catch(() => {});
  };

  useEffect(() => { fetchMeds(); }, []);

  const handleAdd = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/medications`, { name, dose, frequency },
        { headers: { Authorization: `Bearer ${token}` } });
      setName(''); setDose(''); setFrequency('Daily');
      setShowForm(false);
      fetchMeds();
    } catch (err) {}
    setSaving(false);
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API_URL}/api/medications/${id}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setMedications(prev => prev.filter(m => m.id !== id));
    } catch (err) {}
    setDeleting(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <h2 style={{ fontFamily: "'DM Serif Display',serif", color: C.text, fontSize: 22, margin: '0 0 4px' }}>Medications</h2>
        <p style={{ fontSize: 12, color: C.muted }}>Your medications inform personalised insights in Patterns.</p>
      </div>

      {/* Medication list */}
      {medications.length > 0 && (
        <div style={{ background: C.card, borderRadius: 16, padding: 16, border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace', marginBottom: 12 }}>CURRENT MEDICATIONS</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {medications.map((med, idx) => (
              <div key={med.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                paddingBottom: idx < medications.length - 1 ? 10 : 0,
                borderBottom: idx < medications.length - 1 ? `1px solid ${C.border}` : 'none',
              }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: `${C.teal}22`, border: `1px solid ${C.teal}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                  💊
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{med.name}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    {[med.dose, med.frequency].filter(Boolean).join(' · ') || 'No details added'}
                  </div>
                </div>
                <button onClick={() => handleDelete(med.id)} disabled={deleting === med.id}
                  style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 10px', color: C.muted, fontSize: 11, cursor: 'pointer' }}>
                  {deleting === med.id ? '...' : 'Remove'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add form */}
      {showForm ? (
        <div style={{ background: C.card, borderRadius: 16, padding: 16, border: `1px solid ${C.accent}44` }}>
          <div style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace', marginBottom: 12 }}>ADD MEDICATION</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input
              value={name} onChange={e => setName(e.target.value)}
              placeholder="Medication name (e.g. Microgynon, Metformin)"
              style={{ background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.text, fontSize: 13, outline: 'none' }}
            />
            <input
              value={dose} onChange={e => setDose(e.target.value)}
              placeholder="Dose (e.g. 30mg) — optional"
              style={{ background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.text, fontSize: 13, outline: 'none' }}
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {FREQUENCIES.map(f => (
                <button key={f} onClick={() => setFrequency(f)}
                  style={{
                    background: frequency === f ? `${C.teal}22` : C.surface,
                    border: `1.5px solid ${frequency === f ? C.teal : C.border}`,
                    borderRadius: 20, padding: '5px 12px', color: frequency === f ? C.teal : C.muted,
                    fontSize: 11, cursor: 'pointer',
                  }}>
                  {f}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button onClick={handleAdd} disabled={!name.trim() || saving}
                style={{ flex: 1, background: C.accent, color: '#0D0D12', border: 'none', borderRadius: 8, padding: '11px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {saving ? '...' : 'Add'}
              </button>
              <button onClick={() => setShowForm(false)}
                style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, padding: '11px 16px', color: C.muted, fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowForm(true)}
          style={{ background: C.card, border: `1.5px dashed ${C.border}`, borderRadius: 14, padding: '14px', color: C.muted, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>+</span> Add a medication
        </button>
      )}

      {/* Dimensions note */}
      <div style={{ background: `linear-gradient(135deg,#1A1A28,#1C1C2A)`, borderRadius: 14, padding: 14, border: `1px solid ${C.purple}33` }}>
        <div style={{ fontSize: 10, color: C.purple, fontFamily: 'monospace', marginBottom: 6 }}>◈ HOW THIS WORKS</div>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
          Your medications are cross-referenced with peer-reviewed research to surface relevant insights in your Patterns tab — for example, known links between a medication and skin, mood, or cycle symptoms.
        </div>
      </div>
    </div>
  );
}
